import React, { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from './Button';
import InputText from './InputText';
import { getSupabase } from '../utils/supabaseClient';
import { todoLocalRepo } from '../utils/localDb/todoLocalRepo';
import { useSupabaseSession } from '../hooks/useSupabaseSession';
import './PageAuth.css';

type LocationState = { from?: string } | null;

function PageAuth() {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, loading } = useSupabaseSession();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const fromPath = ((location.state as LocationState)?.from ?? '/') as string;

  const canSubmit = email.trim() !== '' && password.trim() !== '' && !submitting;

  const onSubmit = useCallback(async () => {
    setErrorMessage(null);
    setSubmitting(true);
    try {
      const supabase = getSupabase();
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (error) throw error;
      navigate(fromPath, { replace: true });
    } catch (e) {
      setErrorMessage(e instanceof Error ? e.message : 'ログインに失敗しました');
    } finally {
      setSubmitting(false);
    }
  }, [email, password, navigate, fromPath]);

  const onSignOut = useCallback(async () => {
    setErrorMessage(null);
    try {
      const supabase = getSupabase();
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      await todoLocalRepo.clearAll();
    } catch (e) {
      setErrorMessage(e instanceof Error ? e.message : 'ログアウトに失敗しました');
    }
  }, []);

  return (
    <div className="page-auth">
      <div className="page-auth__card">
        <div className="page-auth__title text-heading-large">ログイン</div>

        {loading ? (
          <div className="text-body-medium">読み込み中...</div>
        ) : session ? (
          <>
            <div className="text-body-medium">ログイン済み: {(session.user.email ?? '').toString()}</div>
            <div className="page-auth__actions">
              <Button variant="secondary" onClick={() => navigate('/', { replace: true })}>アプリへ</Button>
              <Button variant="secondary" onClick={onSignOut}>ログアウト</Button>
            </div>
          </>
        ) : (
          <>
            <div className="page-auth__fields">
              <InputText
                type="email"
                hasLeftIcon
                leftIcon="user"
                placeholder="メールアドレス"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
              <InputText
                type="password"
                hasLeftIcon
                leftIcon="id"
                placeholder="パスワード"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
            </div>

            {errorMessage && <div className="page-auth__error text-body-small">{errorMessage}</div>}

            <div className="page-auth__actions">
              <Button onClick={onSubmit} disabled={!canSubmit} loading={submitting}>ログイン</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default PageAuth;
